import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import cloudApi from '../../../services/cloudApi';
import analytics from '../../../services/analytics';
import { useAuth } from '../../../contexts/AuthContext';
import HeroSection from '../BillingView/components/HeroSection';
import BillingToggle from '../BillingView/components/BillingToggle';
import StatusBanners from '../BillingView/components/StatusBanners';
import PlanGrid from '../BillingView/components/PlanGrid';
import FeatureComparisonTable from '../BillingView/components/FeatureComparisonTable';
import TrustBar from '../BillingView/components/TrustBar';
import UsageCalculator from '../BillingView/components/UsageCalculator';
import CreditTopUps from '../BillingView/components/CreditTopUps';
import FAQSection from '../BillingView/components/FAQSection';
import TestimonialsStrip from '../BillingView/components/TestimonialsStrip';
import CheckoutSuccessModal from '../BillingView/components/CheckoutSuccessModal';


const NON_OWNER_TOOLTIP = 'Only the workspace owner can manage subscription options.';

const PlansView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentWorkspace, subscription: billingStatus, refreshSubscription } = useAuth();
  const isOwner = currentWorkspace?.role === 'owner';
  const [plans, setPlans] = useState([]);
  const [creditTopUps, setCreditTopUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [notification, setNotification] = useState(null);
  const [memberBannerDismissed, setMemberBannerDismissed] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(null);

  const [isAnnual, setIsAnnual] = useState(true);
  const [calculatorValue, setCalculatorValue] = useState(3);
  const [showComparison, setShowComparison] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const plansData = await cloudApi.getAvailablePlans();
      setPlans(plansData.plans || []);
      setCreditTopUps(plansData.credit_topups || []);
    } catch (error) {
      console.error('Failed to load plans:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Scroll to hash anchor (e.g. #credit-topups) after data loads
  const location = useLocation();
  useEffect(() => {
    if (!loading && location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [loading, location.hash]);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      const type = searchParams.get('type');
      const plan = searchParams.get('plan');
      const amount = searchParams.get('amount');
      analytics.trackCheckoutCompleted({
        plan,
        source: 'plans',
        flow: 'plans',
        type: type === 'credits' ? 'credits' : 'subscription',
      });
      setCheckoutSuccess(
        type === 'credits'
          ? { type: 'credits', creditAmount: amount }
          : { type: 'subscription', planName: plan }
      );
      setTimeout(() => {
        refreshSubscription();
        loadData();
      }, 2000);
    } else if (checkout === 'cancel') {
      analytics.trackCheckoutCancelled({ flow: 'plans' });
      setNotification({ type: 'info', message: 'Checkout was cancelled.' });
    }
    if (checkout) {
      searchParams.delete('checkout');
      searchParams.delete('session_id');
      searchParams.delete('type');
      searchParams.delete('plan');
      searchParams.delete('amount');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, loadData, refreshSubscription]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const currentPlan = billingStatus?.subscription?.plan?.name || 'free';
  const subscriptionStatus = billingStatus?.subscription?.status;
  const cancelAtPeriodEnd = billingStatus?.subscription?.cancel_at_period_end;

  const handleBuyCredits = async (priceId) => {
    if (!isOwner) {
      setNotification({ type: 'info', message: NON_OWNER_TOOLTIP });
      return;
    }
    setActionLoading(`credits-${priceId}`);
    try {
      const { url } = await cloudApi.createCreditCheckoutSession(priceId);
      analytics.trackCheckoutStarted({ type: 'credits', priceId, source: 'plans', flow: 'plans' });
      window.open(url, '_blank');
    } catch (error) {
      analytics.trackCheckoutFailed({ type: 'credits', priceId, flow: 'plans', reason: error.message });
      setNotification({ type: 'error', message: error.message || 'Failed to start checkout' });
      setActionLoading(null);
    }
  };

  const handleSubscribe = async (planName) => {
    if (!isOwner) {
      setNotification({ type: 'info', message: NON_OWNER_TOOLTIP });
      return;
    }
    setActionLoading(planName);
    const interval = isAnnual ? 'yearly' : 'monthly';
    const selectedPlan = plans.find((p) => p.name === planName) || planName;
    try {
      const { url } = await cloudApi.createCheckoutSession(planName, interval);
      analytics.trackCheckoutStarted({ plan: selectedPlan, interval, source: 'plans', flow: 'plans' });
      window.open(url, '_blank');
    } catch (error) {
      analytics.trackCheckoutFailed({ plan: selectedPlan, interval, flow: 'plans', reason: error.message });
      setNotification({ type: 'error', message: error.message || 'Failed to start checkout' });
      setActionLoading(null);
    }
  };

  const handleOpenPortal = async () => {
    if (!isOwner) {
      setNotification({ type: 'info', message: NON_OWNER_TOOLTIP });
      return;
    }
    setActionLoading('portal');
    try {
      const { url } = await cloudApi.createPortalSession();
      window.open(url, '_blank');
    } catch (error) {
      setNotification({ type: 'error', message: error.message || 'Failed to open billing portal' });
      setActionLoading(null);
    }
  };

  const getPlanAction = (plan) => {
    const isPaid = currentPlan !== 'free';
    const isCurrent = currentPlan === plan.name;

    if (isCurrent) return { label: 'Current Plan', disabled: true };
    const base = isPaid
      ? { label: 'Switch Plan', action: () => handleOpenPortal() }
      : { label: 'Get Started', action: () => handleSubscribe(plan.name) };
    if (!isOwner) return { ...base, disabled: true, tooltip: NON_OWNER_TOOLTIP };
    return base;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="relative max-w-7xl mx-auto pb-12">
      {/* Ambient background orbs — parent card handles overflow clipping */}
      <div className="pointer-events-none absolute -top-32 -left-24 w-[400px] h-[400px] rounded-full bg-blue-500/[0.04] dark:bg-blue-500/[0.07] blur-3xl" />
      <div className="pointer-events-none absolute top-[600px] -right-32 w-[350px] h-[350px] rounded-full bg-purple-500/[0.04] dark:bg-purple-500/[0.06] blur-3xl" />
      <div className="pointer-events-none absolute top-[1200px] -left-24 w-[300px] h-[300px] rounded-full bg-cyan-500/[0.03] dark:bg-cyan-500/[0.05] blur-3xl" />

      {checkoutSuccess && (
        <CheckoutSuccessModal
          type={checkoutSuccess.type}
          plan={plans.find((p) => p.name === checkoutSuccess.planName) || null}
          creditAmount={checkoutSuccess.creditAmount}
          onClose={() => setCheckoutSuccess(null)}
        />
      )}

      {/* Notification Banner */}
      {notification && (
        <div className={`rounded-lg p-4 border animate-fade-in mb-4 ${
          notification.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
          : notification.type === 'error' ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200'
        }`}>
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">{notification.message}</p>
            <button onClick={() => setNotification(null)} className="ml-4 text-sm opacity-60 hover:opacity-100">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {!isOwner && !memberBannerDismissed && (
        <div className="rounded-lg p-4 border animate-fade-in mb-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">
              You're a member of this workspace. {NON_OWNER_TOOLTIP}
            </p>
            <button
              onClick={() => setMemberBannerDismissed(true)}
              className="ml-4 text-sm opacity-60 hover:opacity-100"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <StatusBanners
        subscriptionStatus={subscriptionStatus}
        cancelAtPeriodEnd={cancelAtPeriodEnd}
        billingStatus={billingStatus}
        onOpenPortal={handleOpenPortal}
        disabled={!isOwner}
        disabledTooltip={NON_OWNER_TOOLTIP}
      />

      {/* Hero */}
      <div className="animate-fade-in">
        <HeroSection />
      </div>

      {/* Billing toggle — tight to hero, tight to cards */}
      <div className="animate-fade-in mt-4" style={{ animationDelay: '100ms', animationFillMode: 'backwards' }}>
        <BillingToggle isAnnual={isAnnual} setIsAnnual={setIsAnnual} />
      </div>

      {/* Plan cards */}
      <div className="animate-fade-in mt-8" style={{ animationDelay: '200ms', animationFillMode: 'backwards' }}>
        <PlanGrid
          plans={plans}
          currentPlan={currentPlan}
          isAnnual={isAnnual}
          getPlanAction={getPlanAction}
          actionLoading={actionLoading}
        />
      </div>

      {/* Trust bar — snug below cards */}
      <div className="animate-fade-in mt-4" style={{ animationDelay: '300ms', animationFillMode: 'backwards' }}>
        <TrustBar />
      </div>

      {/* Testimonials */}
      <div className="animate-fade-in mt-14" style={{ animationDelay: '450ms', animationFillMode: 'backwards' }}>
        <TestimonialsStrip />
      </div>

      {/* Feature comparison */}
      <div className="animate-fade-in mt-14" style={{ animationDelay: '550ms', animationFillMode: 'backwards' }}>
        <FeatureComparisonTable
          showComparison={showComparison}
          setShowComparison={setShowComparison}
        />
      </div>

      {/* Usage calculator */}
      <div className="animate-fade-in mt-14" style={{ animationDelay: '650ms', animationFillMode: 'backwards' }}>
        <UsageCalculator
          value={calculatorValue}
          onChange={setCalculatorValue}
          onSelectPlan={(planName) => handleSubscribe(planName)}
        />
      </div>

      {/* Credit top-ups */}
      <div id="credit-topups" className="animate-fade-in mt-14" style={{ animationDelay: '750ms', animationFillMode: 'backwards' }}>
        <CreditTopUps
          creditTopUps={creditTopUps}
          currentPlan={currentPlan}
          onBuyCredits={handleBuyCredits}
          actionLoading={actionLoading}
          disabled={!isOwner}
          disabledTooltip={NON_OWNER_TOOLTIP}
        />
      </div>

      {/* FAQ */}
      <div className="animate-fade-in mt-14" style={{ animationDelay: '850ms', animationFillMode: 'backwards' }}>
        <FAQSection
          openIndex={openFaqIndex}
          setOpenIndex={setOpenFaqIndex}
        />
      </div>
    </div>
  );
};

export default PlansView;
